import React from "react";
import { Box, Typography, Chip, Tooltip } from "@mui/material";
import type { ScorecardBrandMapping } from "@/models/Scorecard";
import { getEffectiveMappingStatus } from "@utils/scorecard-utils";

const STATUS_LABEL: Record<ScorecardBrandMapping["status"], string> = {
  matched: "Matched",
  manual: "Manually mapped",
  unresolved: "Unresolved",
  unavailable: "Unavailable",
};

const STATUS_COLOR: Record<
  ScorecardBrandMapping["status"],
  "success" | "info" | "warning" | "default"
> = {
  matched: "success",
  manual: "info",
  unresolved: "warning",
  unavailable: "default",
};

/** A row's own mapping can look fine while the Scorecard Row Base it
 *  depends on is in a worse state for that same brand — surface the worse
 *  of the two (see getEffectiveMappingStatus) instead of the row's own,
 *  so e.g. the Base being manually mapped isn't hidden behind "Matched". */
function getEffectiveMappingDisplay(
  status: ScorecardBrandMapping["status"],
  baseStatus: ScorecardBrandMapping["status"] | undefined,
): { label: string; color: "success" | "info" | "warning" | "default" } {
  const effective = getEffectiveMappingStatus(status, baseStatus);
  return { label: STATUS_LABEL[effective], color: STATUS_COLOR[effective] };
}

/** Matches the color-swatch + label look used for chart color-coding legends (ColorCodingLegend.tsx). */
export const META_DOT_COLOR = {
  percentage: "#2478FF",
  mean: "#7B1FA2",
  base: "#9AA1AC",
  source: "#5C6470",
} as const;

export const MetaLabel: React.FC<{
  label: string;
  variant: keyof typeof META_DOT_COLOR;
}> = ({ label, variant }) => (
  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: META_DOT_COLOR[variant],
        flexShrink: 0,
      }}
    />
    <Typography
      variant="caption"
      sx={{ color: "#374151", whiteSpace: "nowrap" }}
    >
      {label}
    </Typography>
  </Box>
);

/** Truncates with an ellipsis; the full value shows on hover via Tooltip. */
export const TruncatedText: React.FC<{
  text: string;
  variant?: "body2" | "caption";
  fontWeight?: number;
}> = ({ text, variant = "body2", fontWeight }) => (
  <Tooltip title={text}>
    <Typography
      variant={variant}
      fontWeight={fontWeight}
      noWrap
      sx={{ display: "block", minWidth: 0 }}
    >
      {text}
    </Typography>
  </Tooltip>
);

/** Shared brand-mapping list, used by both row kinds. */
export const BrandMappingsList: React.FC<{
  brandMappings: ScorecardBrandMapping[];
  brandLabelById: Map<string, string>;
  contentIndent: number;
  getBaseStatus?: (brandId: string) => ScorecardBrandMapping["status"] | undefined;
}> = ({ brandMappings, brandLabelById, contentIndent, getBaseStatus }) => (
  <Box sx={{ mt: 0.5, ml: contentIndent, display: "flex", flexDirection: "column", gap: 0.5 }}>
    {brandMappings.map((mapping) => {
      const baseStatus = getBaseStatus?.(mapping.brandId);
      const display = getEffectiveMappingDisplay(mapping.status, baseStatus);
      return (
        <Box
          key={mapping.brandId}
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TruncatedText
              variant="caption"
              text={brandLabelById.get(mapping.brandId) ?? "Unknown brand"}
            />
          </Box>
          <Chip
            label={display.label}
            color={display.color}
            size="small"
            sx={{ height: 20, fontSize: "0.7rem", flexShrink: 0 }}
          />
        </Box>
      );
    })}
  </Box>
);
